import { Heading, Text } from '@components/ui/typography'
import { parse } from '@config/theme'
import { decodeAssetId } from '@lib/sanity/decode-asset-id'
import { urlFor } from '@lib/sanity/url-for'
import BlockContent from '@sanity/block-content-to-react'
import Image from 'next/image'
import React, { FC } from 'react'
import { ChangelogItem as ChangelogItemModel } from '../../__generated__/sanity'
import * as styles from './ChangeLogItem.styles'

type ChangelogItemModelProps = ChangelogItemModel & { isLast?: boolean }

export const ChangelogItem: FC<ChangelogItemModelProps> = ({
  date,
  content,
  image,
  tags,
  isLast,
}) => {
  const d = new Date(date)
  const day = d.toLocaleDateString('us', {
    day: 'numeric',
  })
  const month = d.toLocaleDateString('us', {
    month: 'short',
  })
  const year = d.toLocaleDateString('us', {
    year: 'numeric',
  })
  return (
    <div className={styles.item} data-last={isLast ? '' : undefined}>
      <Heading
        variant="h6"
        as="div"
        color="shade500"
        mb={{ _: '8', md: '0' }}
      >{`${month} ${day}, ${year}`}</Heading>
      <div className={styles.contentContainer}>
        {image && (
          <>
            <Image
              src={urlFor(image).toString()}
              className={styles.img}
              alt=""
              {...decodeAssetId(image.asset._ref).dimensions}
            />
            <div className={parse({ mb: '40' })} />
          </>
        )}
        <BlockContent blocks={content} serializers={serializers} />
        {tags && (
          <div className={parse({ display: 'flex', flexWrap: 'wrap' })}>
            {tags.map((tag, key) => (
              <div key={key} className={styles.tag}>
                {tag}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const serializers = {
  list: function List(props) {
    return <ul className={styles.list}>{props.children}</ul>
  },
  listItem: function ListItem(props) {
    return (
      <Text as="li" variant="bodyRegular">
        {props.children}
      </Text>
    )
  },
  marks: {
    link: function Link(props) {
      return (
        <a target="_blank" href={props.mark.href} rel="noopener noreferrer">
          {props.children}
        </a>
      )
    },
  },
  types: {
    block(props) {
      switch (props.node.style) {
        case 'h2':
          return (
            <Heading variant="h5" as="h2" mb="2">
              {props.children}
            </Heading>
          )
        default:
          return (
            <Text variant="bodyRegular" as="p" mb="8">
              {props.children}
            </Text>
          )
      }
    },
  },
}
